
import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  FileText,
  Edit3,
  Settings,
  ChevronDown,
  LogOut,
  User as UserIcon,
  HelpCircle,
  UtensilsCrossed,
  Star,
  ShoppingBag,
  Receipt,
  UserCog,
  Nfc,
  X,
  Bell,
  QrCode,
} from 'lucide-react'

import { cn } from '../../lib/utils'
import Avatar from '../ui/Avatar'
import { useAuthStore } from '../../stores/authStore'
import api from '../../lib/axios'

const adminMenu = [
  { to: '/app/overview', icon: LayoutDashboard, label: "Vue d'ensemble" },
  { to: '/app/admin/products', icon: ShoppingBag, label: 'Produits (admin)' },
  { to: '/app/admin/orders', icon: Receipt, label: 'Commandes clients (admin)' },
  { to: '/app/admin/subscriptions', icon: UserCog, label: 'Clients & Abonnements' },
  { to: '/app/pages', icon: FileText, label: 'Mes Pages' },
  { to: '/app/pages/new', icon: Edit3, label: 'Éditeur de Page' },
  { to: '/app/menus/new', icon: UtensilsCrossed, label: 'Menu NFC' },
  { to: '/app/menu-orders', icon: Bell, label: 'Commandes du menu' },
  { to: '/app/reviews/new', icon: Star, label: 'Avis Google' },
  { to: '/app/qrs', icon: QrCode, label: 'Générateur QR' },
  { to: '/app/admin/nfc-tools', icon: Nfc, label: 'NFC Tools' },
  { to: '/app/profile', icon: UserIcon, label: 'Mon profil' },
  { to: '/app/settings', icon: Settings, label: 'Paramètres & Support' },
]

const getClientMenu = (hasMenu) => [
  { to: '/app/pages', icon: FileText, label: 'Mes Pages' },
  ...(hasMenu
    ? [{ to: '/app/menu-orders', icon: Bell, label: 'Commandes du menu' }]
    : []),
  { to: '/app/profile', icon: UserIcon, label: 'Mon profil' },
  { to: '/app/settings', icon: Settings, label: 'Paramètres' },
]

function extractPages(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.data?.data)) return data.data.data
  if (Array.isArray(data?.pages)) return data.pages
  return []
}

function userHasMenu(pages) {
  return pages.some((page) => {
    const type = String(page?.type || page?.page_type || '')
      .toLowerCase()
      .trim()

    return type === 'menu'
  })
}

export default function Sidebar({ mobileOpen, onClose }) {
  const navigate = useNavigate()

  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const isAdmin = useAuthStore((state) => state.isAdmin())

  const [menuOpen, setMenuOpen] = useState(false)
  const [hasMenu, setHasMenu] = useState(false)
  const [checkingMenu, setCheckingMenu] = useState(!isAdmin)

  const menuRef = useRef(null)

  useEffect(() => {
    let active = true

    const checkMenu = async () => {
      if (isAdmin || !user) {
        setCheckingMenu(false)
        return
      }

      setCheckingMenu(true)

      try {
        const { data } = await api.get('/pages')
        const pages = extractPages(data)

        if (active) setHasMenu(userHasMenu(pages))
      } catch (error) {
        console.error('Erreur vérification Menu NFC:', error)
        if (active) setHasMenu(false)
      } finally {
        if (active) setCheckingMenu(false)
      }
    }

    checkMenu()

    return () => {
      active = false
    }
  }, [isAdmin, user])

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (mobileOpen) {
      setMenuOpen(false)
    }
  }, [mobileOpen])

  const items = isAdmin ? adminMenu : getClientMenu(hasMenu)

  const handleLogout = async () => {
    if (!window.confirm('Voulez-vous vraiment vous déconnecter ?')) {
      return
    }

    await logout()
    navigate('/login')
  }

  const go = (path) => {
    setMenuOpen(false)
    onClose?.()
    navigate(path)
  }

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          aria-label="Fermer le menu"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-40 flex h-[100dvh] w-64 max-w-[85vw] flex-col overflow-hidden border-r border-gray-100 bg-white',
          'transition-transform duration-300 ease-in-out',
          'lg:translate-x-0 lg:z-20',
          mobileOpen
            ? 'translate-x-0 shadow-2xl'
            : '-translate-x-full'
        )}
        aria-label="Navigation principale"
      >
        <div
          className="relative flex-shrink-0 border-b border-gray-100 p-4"
          ref={menuRef}
        >
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-xl p-2 transition-colors hover:bg-graysoft"
            >
              <Avatar
                name={user?.name || 'Utilisateur'}
                size="md"
              />

              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-sm font-semibold text-navy">
                  {user?.name || 'Utilisateur'}
                </p>
                <p className="truncate text-xs text-gray-500">
                  {user?.email || ''}
                </p>
              </div>

              <ChevronDown
                className={cn(
                  'h-4 w-4 flex-shrink-0 text-gray-400 transition-transform',
                  menuOpen && 'rotate-180'
                )}
              />
            </button>

            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-2 text-gray-500 transition-colors hover:bg-graysoft lg:hidden"
              aria-label="Fermer le menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {menuOpen && (
            <div className="absolute left-4 right-4 top-full z-50 mt-1 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
              <div className="border-b border-gray-100 p-3">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar
                    name={user?.name || 'Utilisateur'}
                    size="md"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-navy">
                      {user?.name || 'Utilisateur'}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {user?.email || ''}
                    </p>
                  </div>
                </div>

                {user?.is_premium && (
                  <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                    ⭐ PREMIUM
                  </span>
                )}
              </div>

              <div className="p-1">
                <button
                  type="button"
                  onClick={() => go('/app/profile')}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-graysoft"
                >
                  <UserIcon className="h-4 w-4 text-gray-500" />
                  Mon profil
                </button>

                <button
                  type="button"
                  onClick={() => go('/app/settings')}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-graysoft"
                >
                  <Settings className="h-4 w-4 text-gray-500" />
                  Paramètres
                </button>

                <button
                  type="button"
                  onClick={() => go('/app/support')}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-graysoft"
                >
                  <HelpCircle className="h-4 w-4 text-gray-500" />
                  Aide & support
                </button>
              </div>

              <div className="border-t border-gray-100 p-1">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                  Se déconnecter
                </button>
              </div>
            </div>
          )}
        </div>

        <nav className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain p-3">
          {!isAdmin && checkingMenu ? (
            <div className="flex items-center justify-center py-6">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-teal/30 border-t-teal" />
            </div>
          ) : (
            <div className="space-y-1">
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/app/pages'}
                  onClick={() => onClose?.()}
                  className={({ isActive }) =>
                    cn(
                      'flex min-w-0 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                      isActive
                        ? 'bg-navy text-white shadow-sm'
                        : 'text-gray-600 hover:bg-graysoft hover:text-navy'
                    )
                  }
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="min-w-0 flex-1 truncate">
                    {item.label}
                  </span>

                  {item.badge && (
                    <span className="h-2 w-2 flex-shrink-0 rounded-full bg-gold" />
                  )}
                </NavLink>
              ))}
            </div>
          )}
        </nav>
      </aside>
    </>
  )
}
